import React, { useState, useEffect } from "react";
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Typography,
  Box,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  LinearProgress,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  Paper,
  Alert,
  Stepper,
  Step,
  StepLabel,
  useTheme,
} from "@mui/material";
import Papa from "papaparse";
import { useSupabase } from "../providers/supabaseProvider";
import { useKindeAuth } from "@kinde-oss/kinde-auth-react";
import { useDispatch, useSelector } from "react-redux";
import {
  importEntriesFromCsv,
  validateEntriesFromCsv,
} from "../features/entry/entrySlice";
import { fetchDistricts } from "../features/bsaData/bsaDataSlice";

// Required field mappings
const REQUIRED_FIELDS = {
  firstName: "First Name",
  lastName: "Last Name",
  bsaNumber: "BSA Number",
  gender: "Gender",
  event: "Event Name",
  event_date: "Event Date",
  category: "Category",
  position: "Position",
  format: "Format",
  categoryType: "Category Type",
};

const OPTIONAL_FIELDS = {
  // Removed: tier, points, district, district_id
};

// Map all possible field name variations to canonical camelCase names
const FIELD_NAME_MAPPING = {
  // categoryType variations
  categorytype: "categoryType",
  category_type: "categoryType",
  "category type": "categoryType",
  cat_type: "categoryType",
  catType: "categoryType",
  // bsaNumber variations
  bsanumber: "bsaNumber",
  bsa_number: "bsaNumber",
  "bsa number": "bsaNumber",
  bsanum: "bsaNumber",

  // firstName variations
  firstname: "firstName",
  first_name: "firstName",
  "first name": "firstName",
  fname: "firstName",

  // lastName variations
  lastname: "lastName",
  last_name: "lastName",
  "last name": "lastName",
  lname: "lastName",

  // eventDate variations
  eventdate: "eventDate",
  event_date: "eventDate",
  "event date": "eventDate",
  date: "eventDate",

  // event variations
  event: "event",
  "event name": "event",
  eventname: "event",

  // category variations
  category: "category",
  cat: "category",

  // position variations
  position: "position",
  pos: "position",

  // format variations
  format: "format",
  fmt: "format",

  // gender variations
  gender: "gender",
  sex: "gender",
};

// Helper to normalize field names from CSV (handles various formats)
const normalizeFieldName = (str) => {
  const normalized = str.trim().toLowerCase();

  // Check direct mapping first
  if (FIELD_NAME_MAPPING[normalized]) {
    return FIELD_NAME_MAPPING[normalized];
  }

  // Fallback: convert space-separated or snake_case to camelCase
  return str
    .trim()
    .split(/[\s_]+/)
    .reduce((result, word, index) => {
      if (index === 0) {
        return word.toLowerCase();
      }
      return (
        result + word.charAt(0).toUpperCase() + word.slice(1).toLowerCase()
      );
    }, "");
};

export default function UploadCsvDialog({ open, onClose, onSuccess }) {
  const supabase = useSupabase();
  const { user } = useKindeAuth();
  const dispatch = useDispatch();
  const theme = useTheme();

  const districts = useSelector((state) => state.bsaData.districts);
  const districtsLoading = useSelector((state) => state.bsaData.isLoading);
  const validationResults = useSelector(
    (state) => state.entry.validationResults,
  );
  const isValidating = useSelector((state) => state.entry.isValidating);

  // Dialog steps: 0 = upload, 1 = map headers, 2 = validate, 3 = progress
  const [step, setStep] = useState(0);
  const [csvFile, setCsvFile] = useState(null);
  const [csvData, setCsvData] = useState([]);
  const [csvHeaders, setCsvHeaders] = useState([]);
  const [headerMapping, setHeaderMapping] = useState({});
  const [validationErrors, setValidationErrors] = useState([]);
  const [validationWarnings, setValidationWarnings] = useState([]);

  const [csvUploading, setCsvUploading] = useState(false);
  const [csvUploadError, setCsvUploadError] = useState(null);
  const [csvUploadSuccess, setCsvUploadSuccess] = useState(null);
  const [selectedDistrict, setSelectedDistrict] = useState("");
  const [uploadProgress, setUploadProgress] = useState(0);

  useEffect(() => {
    if (open) {
      if (!districts || districts.length === 0) {
        dispatch(fetchDistricts());
      }
    }
  }, [open, dispatch]);

  const handleCsvUpload = async (event) => {
    const file = event.target.files[0];
    if (!file) return;

    setCsvFile(file);
    setCsvUploadError(null);

    // Parse CSV to get headers
    Papa.parse(file, {
      header: true,
      skipEmptyLines: true,
      preview: 0,
      complete: (results) => {
        if (results.data.length === 0) {
          setCsvUploadError("CSV file is empty");
          return;
        }

        const headers = (
          results.meta.fields || Object.keys(results.data[0])
        ).filter((header) => header && header.trim() !== "");
        setCsvHeaders(headers);
        setCsvData(results.data);

        // Initialize header mapping with exact matches, defaulting unmapped to empty
        const mapping = {};
        headers.forEach((header) => {
          const normalized = header.toLowerCase().trim();
          const found = Object.keys({
            ...REQUIRED_FIELDS,
            ...OPTIONAL_FIELDS,
          }).find((key) => key.toLowerCase() === normalized);
          mapping[header] = found || ""; // Default to empty string if no match
        });
        setHeaderMapping(mapping);
        setStep(1); // Move to header mapping step
      },
      error: (error) => {
        setCsvUploadError(`CSV parse error: ${error.message}`);
      },
    });
  };

  const handleHeaderMappingChange = (csvHeader, dbField) => {
    setHeaderMapping((prev) => ({
      ...prev,
      [csvHeader]: dbField,
    }));
  };

  const validateData = () => {
    const errors = [];
    const warnings = [];

    if (Object.keys(headerMapping).length === 0) {
      errors.push("No header mappings configured");
      setValidationErrors(errors);
      return false;
    }

    // Check that all required fields are mapped
    const mappedDbFields = Object.values(headerMapping);
    for (const [field] of Object.entries(REQUIRED_FIELDS)) {
      if (!mappedDbFields.includes(field)) {
        errors.push(`Required field "${field}" is not mapped`);
      }
    }

    // Validate row data
    for (let rowIndex = 0; rowIndex < csvData.length; rowIndex++) {
      if (errors.length > 20) break; // Limit error messages

      const row = csvData[rowIndex];
      const mappedRow = {};
      Object.entries(headerMapping).forEach(([csvHeader, dbField]) => {
        mappedRow[dbField] = row[csvHeader];
      });

      // Check required fields have values
      for (const [field, label] of Object.entries(REQUIRED_FIELDS)) {
        if (!mappedRow[field] || mappedRow[field].toString().trim() === "") {
          errors.push(`Row ${rowIndex + 2}: Missing required field "${label}"`);
          if (errors.length > 20) break; // Limit error messages
        }
      }

      // Category Type should be a string or number (ID)
      if (
        mappedRow.categoryType !== undefined &&
        mappedRow.categoryType !== null &&
        mappedRow.categoryType.toString().trim() === ""
      ) {
        errors.push(`Row ${rowIndex + 2}: Category Type is empty`);
      }

      // Validate specific fields
      if (mappedRow.event_date) {
        const dateStr = mappedRow.event_date.toString().trim();
        if (
          !/^\d{4}-\d{2}-\d{2}$/.test(dateStr) &&
          isNaN(Date.parse(dateStr))
        ) {
          warnings.push(
            `Row ${rowIndex + 2}: Event date "${dateStr}" may not be in valid format (YYYY-MM-DD expected)`,
          );
        }
      }

      if (mappedRow.points) {
        const points = parseFloat(mappedRow.points);
        if (isNaN(points) || points < 0) {
          errors.push(
            `Row ${rowIndex + 2}: Points "${mappedRow.points}" is not a valid number`,
          );
        }
      }
    }

    setValidationErrors(errors);
    setValidationWarnings(warnings);

    return errors.length === 0;
  };

  const handleProceedToValidation = async () => {
    if (Object.keys(headerMapping).length === 0) {
      setCsvUploadError("Please map at least one header");
      return;
    }

    // Check that all required fields are mapped
    const mappedDbFields = Object.values(headerMapping).filter((f) => f !== "");
    const requiredFieldKeys = Object.keys(REQUIRED_FIELDS);
    const missingFields = requiredFieldKeys.filter(
      (field) => !mappedDbFields.includes(field),
    );

    if (missingFields.length > 0) {
      setCsvUploadError(
        `Missing required field mappings: ${missingFields.join(", ")}`,
      );
      return;
    }

    // Then do server-side validation via Redux
    setCsvUploadError(null);

    try {
      // Transform CSV data using header mapping and convert to camelCase
      const transformedData = csvData.map((row) => {
        const transformedRow = {};
        Object.entries(headerMapping).forEach(([csvHeader, dbField]) => {
          if (dbField) {
            // Only include mapped fields
            const camelCaseField = normalizeFieldName(dbField);
            transformedRow[camelCaseField] = row[csvHeader];
          }
        });
        return transformedRow;
      });

      const userId = user?.id || user?.user?.id;
      await dispatch(
        validateEntriesFromCsv({
          rows: transformedData,
          userId,
        }),
      ).unwrap();

      setStep(2);
    } catch (error) {
      setCsvUploadError(
        `Server validation error: ${error.message || "Unknown error"}`,
      );
      console.error("Validation error:", error);
    }
  };

  const handleProceedToUpload = async () => {
    if (!validateData()) {
      return;
    }
    setStep(3);
    await processCSV();
  };

  const processCSV = async () => {
    setCsvUploading(true);
    setCsvUploadError(null);
    setCsvUploadSuccess(null);
    setUploadProgress(0);

    try {
      // Upload file to Supabase storage for audit trail
      const { data: storageData, error: storageError } = await supabase.storage
        .from("ranking-imports")
        .upload(`entries/${Date.now()}_${csvFile.name}`, csvFile, {
          cacheControl: "3600",
          upsert: false,
        });

      if (storageError) {
        setCsvUploadError(storageError.message);
        setCsvUploading(false);
        return;
      }

      const importedFileUuid = storageData?.id || null;

      // Transform CSV data using header mapping and convert to camelCase
      const transformedData = csvData.map((row) => {
        const transformedRow = {};
        Object.entries(headerMapping).forEach(([csvHeader, dbField]) => {
          // Convert field name to camelCase for API compatibility
          const camelCaseField = normalizeFieldName(dbField);
          transformedRow[camelCaseField] = row[csvHeader];
        });
        return transformedRow;
      });

      const BATCH_SIZE = 100;
      const errors = [];
      let successCount = 0;
      let processedBatches = 0;
      const totalBatches = Math.ceil(transformedData.length / BATCH_SIZE);

      const districtObj = districts.find(
        (d) => d.districtId === selectedDistrict,
      );
      const districtIdToSend = selectedDistrict
        ? districtObj?.districtId || selectedDistrict
        : null;
      const districtNameToSend = selectedDistrict
        ? districtObj?.districtName || districtObj?.name || ""
        : null;

      for (
        let offset = 0;
        offset < transformedData.length;
        offset += BATCH_SIZE
      ) {
        const batch = transformedData.slice(offset, offset + BATCH_SIZE);
        try {
          const userId = user?.id || user?.user?.id || null;

          const result = await dispatch(
            importEntriesFromCsv({
              rows: batch,
              offset,
              importedFileUuid,
              userId,
              districtId: districtIdToSend,
              district: districtNameToSend,
            }),
          ).unwrap();

          successCount += result?.successCount || 0;
          if (result?.errors) {
            errors.push(...result.errors);
          }

          processedBatches++;
          setUploadProgress(
            Math.round((processedBatches / totalBatches) * 100),
          );

          setCsvUploadSuccess(
            `Processing... Batch ${processedBatches}/${totalBatches} (${Math.round((processedBatches / totalBatches) * 100)}%)`,
          );
        } catch (e) {
          errors.push({ offset, error: e.message });
          console.error("Error importing batch:", e);
        }
      }

      setCsvUploadSuccess(
        `Completed: ${successCount} rows imported, ${errors.length} errors.` +
          (errors.length > 0 ? ` Errors: ${JSON.stringify(errors)}` : ""),
      );
      setUploadProgress(100);
      setCsvUploading(false);
      if (onSuccess) onSuccess();
    } catch (err) {
      setCsvUploadError(err.message);
      setCsvUploading(false);
    }
  };

  const handleReset = () => {
    setStep(0);
    setCsvFile(null);
    setCsvData([]);
    setCsvHeaders([]);
    setHeaderMapping({});
    setValidationErrors([]);
    setValidationWarnings([]);
    setCsvUploadError(null);
    setCsvUploadSuccess(null);
    setUploadProgress(0);
  };

  const handleClose = () => {
    if (step !== 3 || !csvUploading) {
      handleReset();
      onClose();
    }
  };

  return (
    <Dialog open={open} onClose={handleClose} maxWidth="md" fullWidth>
      <DialogTitle>Bulk Upload Entries via CSV</DialogTitle>
      <DialogContent dividers>
        <Box sx={{ mb: 3 }}>
          <Stepper activeStep={step}>
            <Step>
              <StepLabel>Upload</StepLabel>
            </Step>
            <Step>
              <StepLabel>Map Headers</StepLabel>
            </Step>
            <Step>
              <StepLabel>Validate</StepLabel>
            </Step>
            <Step>
              <StepLabel>Import</StepLabel>
            </Step>
          </Stepper>
        </Box>

        {/* Step 0: Upload CSV */}
        {step === 0 && (
          <Box>
            <FormControl fullWidth sx={{ mb: 2 }}>
              <InputLabel id="district-select-label">
                District (Optional)
              </InputLabel>
              <Select
                labelId="district-select-label"
                value={selectedDistrict}
                label="District (Optional)"
                onChange={(e) => setSelectedDistrict(e.target.value)}
              >
                <MenuItem value="">None</MenuItem>
                {Array.isArray(districts) &&
                  districts.map((district) => (
                    <MenuItem
                      key={district.districtId}
                      value={district.districtId}
                    >
                      {district.districtName || district.districtId}
                    </MenuItem>
                  ))}
              </Select>
            </FormControl>
            <Typography variant="h6" sx={{ mb: 2 }}>
              Select a CSV file to upload
            </Typography>
            <Button
              variant="contained"
              component="label"
              color="primary"
              fullWidth
              sx={{ mb: 2 }}
            >
              Choose CSV File
              <input
                type="file"
                accept=".csv"
                hidden
                onChange={handleCsvUpload}
              />
            </Button>
          </Box>
        )}

        {/* Step 1: Map Headers */}
        {step === 1 && (
          <Box>
            <Typography variant="h6" sx={{ mb: 2 }}>
              Map CSV Headers to Entry Fields
            </Typography>
            <Alert severity="info" sx={{ mb: 2 }}>
              Select the database field that corresponds to each CSV column.
              Required fields must be mapped.
            </Alert>
            <Table size="small">
              <TableHead>
                <TableRow
                  sx={{
                    backgroundColor:
                      theme.palette.mode === "dark"
                        ? theme.palette.grey[800]
                        : theme.palette.grey[200],
                  }}
                >
                  <TableCell
                    sx={{
                      fontWeight: "bold",
                      color: theme.palette.text.primary,
                    }}
                  >
                    CSV Header
                  </TableCell>
                  <TableCell
                    sx={{
                      fontWeight: "bold",
                      color: theme.palette.text.primary,
                    }}
                  >
                    Database Field
                  </TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {csvHeaders
                  .filter((header) => header && header.trim() !== "")
                  .map((header) => (
                    <TableRow key={header}>
                      <TableCell sx={{ fontFamily: "monospace" }}>
                        {header}
                      </TableCell>
                      <TableCell>
                        <Select
                          value={headerMapping[header] || ""}
                          onChange={(e) =>
                            handleHeaderMappingChange(header, e.target.value)
                          }
                          size="small"
                          fullWidth
                        >
                          <MenuItem value="">-- Do not import --</MenuItem>
                          {Object.entries(REQUIRED_FIELDS).map(
                            ([field, label]) => {
                              // Check if this field is already mapped to a different header
                              const isAlreadyMapped = Object.entries(
                                headerMapping,
                              ).some(
                                ([mappedHeader, mappedField]) =>
                                  mappedField === field &&
                                  mappedHeader !== header,
                              );

                              return isAlreadyMapped ? null : (
                                <MenuItem key={field} value={field}>
                                  {label} (required)
                                </MenuItem>
                              );
                            },
                          )}
                          {Object.entries(OPTIONAL_FIELDS).map(
                            ([field, label]) => {
                              // Check if this field is already mapped to a different header
                              const isAlreadyMapped = Object.entries(
                                headerMapping,
                              ).some(
                                ([mappedHeader, mappedField]) =>
                                  mappedField === field &&
                                  mappedHeader !== header,
                              );

                              return isAlreadyMapped ? null : (
                                <MenuItem key={field} value={field}>
                                  {label}
                                </MenuItem>
                              );
                            },
                          )}
                        </Select>
                      </TableCell>
                    </TableRow>
                  ))}
              </TableBody>
            </Table>
          </Box>
        )}

        {/* Step 2: Validate */}
        {step === 2 && (
          <Box>
            <Typography variant="h6" sx={{ mb: 2 }}>
              Data Validation
            </Typography>

            {isValidating && (
              <Alert severity="info" sx={{ mb: 2 }}>
                <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                  <LinearProgress sx={{ flex: 1 }} />
                  <Typography variant="body2">Validating...</Typography>
                </Box>
              </Alert>
            )}

            {/* Server Validation Results */}
            {validationResults && !isValidating && (
              <Paper
                sx={{
                  p: 2,
                  mb: 2,
                  backgroundColor:
                    theme.palette.mode === "dark"
                      ? theme.palette.grey[900]
                      : theme.palette.grey[50],
                }}
              >
                <Typography
                  variant="subtitle1"
                  sx={{ fontWeight: "bold", mb: 2 }}
                >
                  Import Summary
                </Typography>
                <Box
                  sx={{
                    display: "grid",
                    gridTemplateColumns: "repeat(3, 1fr)",
                    gap: 2,
                    mb: 2,
                  }}
                >
                  <Box
                    sx={{
                      p: 1.5,
                      backgroundColor: theme.palette.success.light,
                      borderRadius: 1,
                    }}
                  >
                    <Typography
                      variant="body2"
                      sx={{ color: theme.palette.success.dark }}
                    >
                      Rows to Import
                    </Typography>
                    <Typography
                      variant="h6"
                      sx={{
                        color: theme.palette.success.dark,
                        fontWeight: "bold",
                      }}
                    >
                      {typeof validationResults.validRows === "number"
                        ? validationResults.validRows
                        : 0}
                    </Typography>
                  </Box>
                  <Box
                    sx={{
                      p: 1.5,
                      backgroundColor: theme.palette.warning.light,
                      borderRadius: 1,
                    }}
                  >
                    <Typography
                      variant="body2"
                      sx={{ color: theme.palette.warning.dark }}
                    >
                      Rows with Errors
                    </Typography>
                    <Typography
                      variant="h6"
                      sx={{
                        color: theme.palette.warning.dark,
                        fontWeight: "bold",
                      }}
                    >
                      {Array.isArray(validationResults.skipRows)
                        ? validationResults.skipRows.length
                        : 0}
                    </Typography>
                  </Box>
                  <Box
                    sx={{
                      p: 1.5,
                      backgroundColor: theme.palette.error.light,
                      borderRadius: 1,
                    }}
                  >
                    <Typography
                      variant="body2"
                      sx={{ color: theme.palette.error.dark }}
                    >
                      Duplicates Found
                    </Typography>
                    <Typography
                      variant="h6"
                      sx={{
                        color: theme.palette.error.dark,
                        fontWeight: "bold",
                      }}
                    >
                      {Array.isArray(validationResults.duplicates)
                        ? validationResults.duplicates.length
                        : 0}
                    </Typography>
                  </Box>
                </Box>

                {validationResults.duplicates &&
                  validationResults.duplicates.length > 0 && (
                    <Alert severity="error" sx={{ mb: 2 }}>
                      <Typography
                        variant="body2"
                        sx={{ fontWeight: "bold", mb: 1 }}
                      >
                        {validationResults.duplicates.length} duplicate(s) found
                        (will be skipped):
                      </Typography>
                      <Box
                        sx={{
                          fontSize: "0.875rem",
                          maxHeight: "150px",
                          overflow: "auto",
                        }}
                      >
                        {validationResults.duplicates.map((dup, idx) => (
                          <Typography
                            key={idx}
                            variant="body2"
                            sx={{ mb: 0.5 }}
                          >
                            • Event: {dup.event}, Date:{" "}
                            {dup.eventDate || dup.event_date}, Player:{" "}
                            {dup.player}
                          </Typography>
                        ))}
                      </Box>
                    </Alert>
                  )}

                {validationResults.skipRows &&
                  validationResults.skipRows.length > 0 && (
                    <Alert severity="warning" sx={{ mb: 2 }}>
                      <Typography
                        variant="body2"
                        sx={{ fontWeight: "bold", mb: 1 }}
                      >
                        {validationResults.skipRows.length} row(s) with
                        validation errors:
                      </Typography>
                      <Box
                        sx={{
                          fontSize: "0.875rem",
                          maxHeight: "150px",
                          overflow: "auto",
                        }}
                      >
                        {validationResults.skipRows
                          .slice(0, 15)
                          .map((skip, idx) => (
                            <Typography
                              key={idx}
                              variant="body2"
                              sx={{ mb: 0.5 }}
                            >
                              • Row {skip.rowIndex}: {skip.reason || skip.error}
                            </Typography>
                          ))}
                        {validationResults.skipRows.length > 15 && (
                          <Typography variant="body2">
                            ... and {validationResults.skipRows.length - 15}{" "}
                            more
                          </Typography>
                        )}
                      </Box>
                    </Alert>
                  )}
              </Paper>
            )}

            {/* Client-side validation errors/warnings */}
            {validationErrors.length > 0 && (
              <Alert severity="error" sx={{ mb: 2 }}>
                <Typography variant="body2" sx={{ fontWeight: "bold", mb: 1 }}>
                  {validationErrors.length} client-side validation error(s)
                  found:
                </Typography>
                <Box
                  component="div"
                  sx={{
                    maxHeight: "200px",
                    overflow: "auto",
                    fontSize: "0.875rem",
                  }}
                >
                  {validationErrors.slice(0, 20).map((error, idx) => (
                    <div key={idx}>• {error}</div>
                  ))}
                  {validationErrors.length > 20 && (
                    <div>... and {validationErrors.length - 20} more</div>
                  )}
                </Box>
              </Alert>
            )}
            {validationWarnings.length > 0 && (
              <Alert severity="warning" sx={{ mb: 2 }}>
                <Typography variant="body2" sx={{ fontWeight: "bold", mb: 1 }}>
                  {validationWarnings.length} warning(s):
                </Typography>
                <Box
                  component="div"
                  sx={{
                    maxHeight: "200px",
                    overflow: "auto",
                    fontSize: "0.875rem",
                  }}
                >
                  {validationWarnings.slice(0, 10).map((warning, idx) => (
                    <div key={idx}>⚠ {warning}</div>
                  ))}
                </Box>
              </Alert>
            )}
            {validationErrors.length === 0 &&
              validationWarnings.length === 0 &&
              validationResults && (
                <Alert severity="success" sx={{ mb: 2 }}>
                  ✓ Validation complete - ready to import
                </Alert>
              )}
            <Typography variant="subtitle2" sx={{ mt: 2, mb: 1 }}>
              Preview (first 5 rows):
            </Typography>
            <Paper sx={{ overflow: "auto", maxHeight: "300px" }}>
              <Table size="small">
                <TableHead>
                  <TableRow
                    sx={{
                      backgroundColor:
                        theme.palette.mode === "dark"
                          ? theme.palette.grey[800]
                          : theme.palette.grey[200],
                    }}
                  >
                    {Object.entries(headerMapping)
                      .filter(([_, dbField]) => dbField && dbField !== "")
                      .map(([csvHeader, dbField]) => (
                        <TableCell
                          key={csvHeader}
                          sx={{
                            fontWeight: "bold",
                            minWidth: "100px",
                            color: theme.palette.text.primary,
                          }}
                        >
                          {dbField}
                        </TableCell>
                      ))}
                  </TableRow>
                </TableHead>
                <TableBody>
                  {csvData.slice(0, 5).map((row, idx) => (
                    <TableRow key={idx}>
                      {Object.entries(headerMapping)
                        .filter(([_, dbField]) => dbField && dbField !== "")
                        .map(([csvHeader, dbField]) => (
                          <TableCell
                            key={csvHeader}
                            sx={{ fontSize: "0.875rem" }}
                          >
                            {row[csvHeader]}
                          </TableCell>
                        ))}
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </Paper>
          </Box>
        )}

        {/* Step 3: Progress */}
        {step === 3 && (
          <Box>
            <Typography variant="h6" sx={{ mb: 2 }}>
              Importing Data
            </Typography>
            {csvUploading && (
              <Box sx={{ mb: 2 }}>
                <LinearProgress
                  variant="determinate"
                  value={uploadProgress}
                  sx={{ mb: 1 }}
                />
                <Typography variant="body2">{uploadProgress}%</Typography>
              </Box>
            )}
            {csvUploadSuccess && (
              <Alert severity="success" sx={{ mb: 2 }}>
                {csvUploadSuccess}
              </Alert>
            )}
            {csvUploadError && (
              <Alert severity="error" sx={{ mb: 2 }}>
                {csvUploadError}
              </Alert>
            )}
          </Box>
        )}

        {csvUploadError && step < 3 && (
          <Alert severity="error" sx={{ mt: 2 }}>
            {csvUploadError}
          </Alert>
        )}
      </DialogContent>
      <DialogActions>
        <Button onClick={handleClose} disabled={csvUploading}>
          {step === 3 && csvUploading ? "Processing..." : "Cancel"}
        </Button>
        {step === 1 && <Button onClick={() => setStep(0)}>Back</Button>}
        {step === 2 && (
          <Button
            onClick={() => {
              setStep(1);
            }}
          >
            Back
          </Button>
        )}
        {step === 1 && (
          <Button
            onClick={handleProceedToValidation}
            variant="contained"
            disabled={Object.keys(headerMapping).length === 0 || isValidating}
          >
            {isValidating ? "Validating..." : "Next: Validate"}
          </Button>
        )}
        {step === 2 &&
          validationErrors.length === 0 &&
          validationResults &&
          typeof validationResults.validRows === "number" &&
          validationResults.validRows > 0 && (
            <Button
              onClick={handleProceedToUpload}
              variant="contained"
              color="success"
              disabled={csvUploading}
            >
              Import Data
            </Button>
          )}
      </DialogActions>
    </Dialog>
  );
}
